import { PureComponent, ReactNode } from 'react';
import Fuse from 'fuse.js';
import { GRefer } from '../../../store/db';
import { getDoctorProfile, getSelectedClinicId } from '../../../store/idbHandler';
import PadV2AutoSuggest, {
    AS_OPTIONS_RECENTLY,
    AS_OPTIONS_SEARCH,
    AllOptions,
} from '../rightPanel/workspace/padV2/PadV2AutoSuggest';
import AddDoctorDialog from '../rightPanel/workspace/AddDoctorDialog';
import { LOCAL_ID_PREFIX } from '../../../constants';
import { hashCode } from '../../../store/utils';
import {
    API_SEARCH,
    AS_EMPTY,
    AS_SEARCH,
    CUSTOM,
    GENIE,
    LABEL_TYPE,
    OPTION_SOURCE,
    SUGGESTED,
} from '../trackSource';
import { SpecialityEntity } from '../../../api/fetchSpecialityForDoctor';
import fetchDoctorAutoSuggest, { DoctorResponse } from '../../../api/fetchDoctorAutoSuggest';
import fetchDoctorProfile, { DocProfileResponse } from '../../../api/fetchDoctorProfile';
import { track } from '../../../tracking';
import PadGenieAutoSuggestFuseManager, { REFER } from '../PadGenieAutoSuggestFuseManager';

export const REFERRAL_AS_EL_ID = 'REFERRAL_AS_EL_ID';
export const REFERRAL_AS_INPUT = 'REFERRAL_AS_INPUT';

type Props = {
    onSelect: (option: GReferWithTrack) => void;
    elements: GRefer[];
    specialities?: SpecialityEntity[] | null;
    onSearch?: (
        input: string,
    ) => { id: string; name: string; specilization: string; type?: 'doctor' | 'speciality' }[];
    mobileOptional?: boolean;
    docid: string;
    searchFuse?: Fuse<GReferWithTrack>;
    allGenieListItems?: GReferWithTrack[];
};

type State = {
    inputValue: string;
    options: GReferOptions;
    isAddDoctorDialog?: GReferWithTrack;
};

type GReferOptions = AllOptions<GReferWithTrack>;

export type GReferType = GRefer & { name: string };

export type DoctorReferClinicAddress = {
    _id?: string;
    name?: string;
    line?: string;
    landmark?: string;
    city?: string;
    state?: string;
    city_id?: string;
    place_id?: string;
    pincode?: string;
    coordinates?: string;
    gplacesid?: string;
};

export type GReferWithTrack = GReferType & {
    referred_doc_profile?: DocProfileResponse;
    track?: {
        label: LABEL_TYPE;
        index: number;
        source: OPTION_SOURCE;
    };
};

class DoctorReferralSearchInput extends PureComponent<Props, State> {
    selectedClinic = getSelectedClinicId();
    state: State = {
        inputValue: '',
        options: [],
        isAddDoctorDialog: undefined,
    };

    getOptions = async (
        setOptions: (options: GReferOptions, inputValue?: string) => void,
        input?: string,
    ): Promise<void> => {
        const { elements, onSearch, searchFuse, allGenieListItems } = this.props;
        const { inputValue } = this.state;

        if (input && inputValue === input) {
            return;
        }

        const fuse =
            searchFuse ||
            (PadGenieAutoSuggestFuseManager.getFuseForType(REFER) as Fuse<GReferWithTrack>);
        const gElements =
            allGenieListItems ||
            ((PadGenieAutoSuggestFuseManager.getOriginalListForType(REFER) ||
                []) as GReferWithTrack[]);

        if (!fuse && !onSearch) return;

        if (!input) {
            const genieOptions =
                gElements
                    ?.map((element, index) => ({
                        ...element,
                        doc: {
                            s_name: element?.s_name,
                            s_id: element?.s_id,
                            ...element.doc,
                        },
                        track: {
                            label: AS_EMPTY,
                            index: index,
                            source: SUGGESTED,
                        },
                    }))
                    ?.filter(Boolean)
                    ?.filter((e) => !elements.find((t) => t.id === e.id)) || [];

            genieOptions.splice(7);

            const currentOptions = [
                {
                    label: AS_OPTIONS_RECENTLY as typeof AS_OPTIONS_SEARCH,
                    options: genieOptions.map((option) => ({
                        ...option,
                        name: [option?.doc?.name?.f, option?.doc?.name?.l]
                            .filter(Boolean)
                            .join(' '),
                    })),
                },
            ];
            setOptions(currentOptions, input);
        } else {
            const filteredGItems = fuse
                .search(input)
                .map((option, index) => ({
                    ...option.item,
                    track: {
                        label: AS_SEARCH,
                        index: index,
                        source: GENIE,
                    },
                }))
                .filter(Boolean)
                .filter((e) => !elements.find((t) => t.id === e.id));

            filteredGItems.splice(7);

            const currentOptions = [
                {
                    label: AS_OPTIONS_SEARCH as typeof AS_OPTIONS_SEARCH,
                    options: filteredGItems.map((option) => ({
                        ...option,
                        name: [option?.doc?.name?.f, option?.doc?.name?.l]
                            .filter(Boolean)
                            .join(' '),
                    })),
                },
            ];

            setOptions(currentOptions, input);

            try {
                let doctorSuggestion: DoctorResponse[] | undefined;
                if (onSearch) {
                    doctorSuggestion = onSearch(input).map((doctor) => ({
                        ...doctor,
                        oid: doctor.id,
                        name: doctor.name,
                        major_specialisation: doctor.specilization,
                        type: doctor.type,
                    }));
                } else {
                    doctorSuggestion = (await fetchDoctorAutoSuggest(input)).data;
                }
                this.handleApiOptions(currentOptions, input, setOptions, doctorSuggestion);
            } catch (error) {
                this.handleApiOptions(currentOptions, input, setOptions);
            }
        }

        this.setState({ inputValue: input || '' });
    };

    handleApiOptions = async (
        currentOptions: GReferOptions,
        input: string,
        setOptions: (options: GReferOptions, inputValue?: string) => void,
        response?: DoctorResponse[],
    ): Promise<void> => {
        const { onSearch, docid } = this.props;
        const allOptions = [...currentOptions[0].options];
        const docProfile = await getDoctorProfile(docid);
        console.log('docProfileresponse', response);
        response?.filter(Boolean).forEach((doctor, i) => {
            const speciality = this.props?.specialities?.find(
                (speciality) =>
                    speciality?.name?.toLowerCase() === doctor?.major_specialisation?.toLowerCase(),
            );

            const selectedClinicData = docProfile?.profile.professional.clinics?.find(
                (clinic) => clinic._id === this.selectedClinic,
            );
            const clinic =
                doctor?.profile?.professional?.clinics?.find(
                    (clinic) =>
                        clinic.city_id === selectedClinicData?.city_id ||
                        clinic.city_name === selectedClinicData?.city_name,
                ) || doctor?.profile?.professional?.clinics?.[0];

            if (speciality) {
                allOptions.push({
                    id: doctor?.oid,
                    name: doctor?.name || '',

                    doc: {
                        id: doctor?.oid,
                        s_name: doctor.major_specialisation,
                        s_id: speciality.id.toString() || doctor.s_id,
                        type: doctor.type,
                        name: doctor?.name
                            ? {
                                  f: doctor?.name || '',
                                  l: '',
                              }
                            : undefined,
                        phone: {
                            code: '',
                            value: '',
                        },
                        clinic: clinic?.address
                            ? {
                                  _id: clinic?._id,
                                  name: clinic?.name,
                                  line: clinic?.address?.line1,
                                  landmark: clinic?.address?.line1,
                                  city: clinic?.address?.city,
                                  state: clinic?.address?.state,
                                  city_id: clinic?.city_id,
                                  pincode: clinic?.address?.pin,
                                  coordinates: clinic?.location?.join(','),
                                  gplacesid: clinic?.gplacesid,
                              }
                            : undefined,
                    },
                    track: {
                        label: AS_SEARCH,
                        index: allOptions?.length,
                        source: API_SEARCH,
                    },
                });
            } else {
                allOptions.push({
                    id: doctor?.oid,
                    name: doctor?.name || '',

                    doc: {
                        id: doctor?.oid,
                        s_name: doctor.major_specialisation,
                        s_id: doctor.s_id,
                        type: doctor.type,
                        name: doctor?.name
                            ? {
                                  f: doctor?.name || '',
                                  l: '',
                              }
                            : undefined,
                        phone: {
                            code: '',
                            value: '',
                        },
                        clinic: clinic?.address
                            ? {
                                  _id: clinic?._id,
                                  name: clinic?.name,
                                  line: clinic?.address?.line1,
                                  landmark: clinic?.address?.line1,
                                  city: clinic?.address?.city,
                                  state: clinic?.address?.state,
                                  city_id: clinic?.city_id,
                                  pincode: clinic?.address?.pin,
                                  coordinates: clinic?.location?.join(','),
                                  gplacesid: clinic?.gplacesid,
                              }
                            : undefined,
                    },
                    track: {
                        label: AS_SEARCH,
                        index: allOptions?.length,
                        source: API_SEARCH,
                    },
                });
            }
        });

        const nonSpecialityOptions = allOptions
            .filter((t) => t.doc.type !== 'speciality')
            .slice(0, 7);

        if (input && input.length >= 3 && !onSearch) {
            nonSpecialityOptions.push({
                id: `${LOCAL_ID_PREFIX}${hashCode(input.trim())}`,
                s_name: '',
                s_id: '',
                name: input,
                doc: {
                    name: {
                        f: input,
                        l: '',
                    },
                },
                track: {
                    label: AS_SEARCH,
                    index: allOptions?.length,
                    source: CUSTOM,
                },
            });
        }

        const specialityOptions = allOptions.filter((t) => t.doc.type === 'speciality').slice(0, 7);

        setOptions(
            [
                {
                    label: AS_OPTIONS_SEARCH,
                    options: nonSpecialityOptions,
                },
                {
                    label: AS_OPTIONS_SEARCH,
                    options: specialityOptions,
                },
            ],
            input,
        );
    };

    saveNewDoctor = (option: GReferWithTrack) => {
        this.props.onSelect(option);
    };

    fillDoctorDetails = async (option: GReferWithTrack) => {
        try {
            const docId = option?.doc.id;
            const doctorProfile = await fetchDoctorProfile(docId);
            const clinic = doctorProfile?.profile?.professional?.clinics?.[0];

            const speciality = this.props?.specialities?.find(
                (speciality) =>
                    speciality?.name?.toLowerCase() === option.doc.s_name?.toLowerCase(),
            );

            this.setState({
                isAddDoctorDialog: {
                    ...option,
                    doc: {
                        ...option.doc,
                        s_id: speciality?.id.toString() || option.doc.s_id,

                        clinic: {
                            _id: clinic?._id,
                            name: clinic?.name,
                            line: clinic?.address?.line1,
                            landmark: clinic?.address?.line1,
                            city: clinic?.address?.city,
                            state: clinic?.address?.state,
                            city_id: clinic?.city_id,
                            pincode: clinic?.address?.pin,
                            coordinates: clinic?.location?.join(','),
                            gplacesid: clinic?.gplacesid,
                        },
                    },
                    referred_doc_profile: doctorProfile,
                },
            });
        } catch (error) {
            console.error(error);
        }
    };

    onSave = (option: any) => {
        const { mobileOptional } = this.props;

        if (option.track?.source === API_SEARCH && !mobileOptional) {
            try {
                this.setState(
                    {
                        isAddDoctorDialog: option,
                    },
                    () => {
                        this.fillDoctorDetails(option);
                    },
                );
            } catch (error) {
                console.error((error as Error).message);
                track('ERROR', { on: 'ABHADashboard => fetchDoctorProfile' });
            }

            return;
        }

        const isAddNewEntity =
            option.id.startsWith(LOCAL_ID_PREFIX) && option?.track?.source === CUSTOM;

        if (isAddNewEntity && !mobileOptional) {
            this.setState({ isAddDoctorDialog: option });
        } else {
            this.props.onSelect(option);
        }
    };

    onCloseDoctorDialog = () => {
        this.setState({ isAddDoctorDialog: undefined });
    };

    render(): ReactNode {
        const { isAddDoctorDialog } = this.state;
        const { specialities } = this.props;

        if (isAddDoctorDialog) {
            return (
                <div className="w-full shadow-800 border border-darwin-neutral-200">
                    <AddDoctorDialog
                        doctorData={isAddDoctorDialog}
                        onCloseDoctorDialog={this.onCloseDoctorDialog}
                        specialities={specialities}
                        onSave={this.props.onSelect}
                        isNewReferral={true}
                    />
                </div>
            );
        }

        return (
            <PadV2AutoSuggest
                inputId={REFERRAL_AS_INPUT}
                elId={REFERRAL_AS_EL_ID}
                onSelect={this.onSave}
                optionIconText={<></>}
                optionIconBgClass="circle-doctor-referral"
                menuListClassName="doctor-referral-dropdown"
                optionWrapperClass="doctor-referral-dropdown-item"
                getOptions={this.getOptions}
                placeholder="Start typing doctor name or speciality.."
                elements={[]}
                autosuggestHeading="Referred to"
            />
        );
    }
}

export default DoctorReferralSearchInput;
